import { useEffect, useMemo, useState } from 'react';

import { realtimeLog } from './debug';
import { getPresenceStore, refreshPresence } from './presenceBridge';
import { PresenceEntry } from './PresenceStore';

export interface PresenceView {
  snapshot: ReadonlyMap<string, PresenceEntry>;
  /**
   * Same contract as PresenceStore.getPresence: true/false once the store
   * knows (an event for this uuid, or any hereNow seed — after a seed an
   * unknown uuid is authoritatively absent); undefined only while the store
   * is unseeded (hereNow pending or failed). Callers keep their legacy
   * presence value for undefined, so a hereNow failure degrades to today's
   * behaviour instead of blanking the dots.
   */
  getPresence(uuid: string): boolean | undefined;
}

/**
 * React adapter over the per-channel PresenceStore. Re-renders on presence
 * change; seeds the store from hereNow on mount so the first paint has
 * authoritative state.
 *
 * Pass undefined while the channel name isn't known yet (e.g. room still
 * loading) — returns null and subscribes to nothing.
 */
export function usePresence(channel: string | undefined): PresenceView | null {
  const store = useMemo(
    () => (channel ? getPresenceStore(channel) : null),
    [channel]
  );

  const [snapshot, setSnapshot] = useState<ReadonlyMap<
    string,
    PresenceEntry
  > | null>(() => store?.snapshot() ?? null);

  useEffect(() => {
    if (!channel || !store) {
      setSnapshot(null);
      return;
    }

    setSnapshot(store.snapshot());
    // The channel manager seeds the stores of the channels it subscribes;
    // only ask for a seed when nothing has yet (each hereNow is billable).
    if (!store.isSeeded()) {
      realtimeLog(`usePresence: mounted for ${channel}, requesting seed`);
      void refreshPresence(channel);
    }

    return store.subscribe(() => {
      setSnapshot(store.snapshot());
    });
  }, [channel, store]);

  return useMemo(() => {
    if (!snapshot || !store) {
      return null;
    }
    return {
      snapshot,
      // Delegate rather than re-read the snapshot map: the map alone can't
      // express "seeded ⇒ unknown uuid is absent", and both consumers rely
      // on that to show a never-connected rostered student as disconnected.
      // The store emits on every seed/change, so `snapshot` (and therefore
      // this memo) refreshes whenever the answer could differ.
      getPresence: (uuid: string) => store.getPresence(uuid),
    };
  }, [snapshot, store]);
}
